import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, forkJoin } from 'rxjs';

import { PresalesService } from '../../services/presales.service';
import { PresalesProject, Currency, CurrencyRate, SalesStage, buildRateMap, toUsd } from '../../models/presales.model';

interface KpiCard {
  label: string;
  value: string;
  sublabel: string;
  icon: string;
  styleClass: string;
}

interface HealthRow {
  code: string;
  name: string;
  client: string;
  pm: string;
  accountManager: string;
  stage: SalesStage;
  currency: Currency;
  revenue: number;
  winning: number;
  startDate: string | null;
  endDate: string | null;
  expectedClose: string | null;
}

/**
 * Presales Summary — reuses the Portfolio Overview layout (KPI cards row +
 * three donut charts + a table). Aggregates every presales project the
 * current user can see. Read-only; no interactions here — deep-links happen
 * elsewhere (Inventory / Details).
 */
@Component({
  selector: 'app-presales-summary',
  standalone: true,
  imports: [CommonModule, TableModule, UIChart, Tag, Tooltip],
  providers: [DatePipe],
  templateUrl: './presales-summary.component.html',
  styleUrl: './presales-summary.component.scss'
})
export class PresalesSummaryComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  loading = true;
  cards: KpiCard[] = [];

  stageChartData: any = { labels: [], datasets: [] };
  pmChartData: any = { labels: [], datasets: [] };
  currencyChartData: any = { labels: [], datasets: [] };
  chartOptions: any = this.buildChartOptions();

  /** True when the chart has at least one non-zero data point. Used by the
   *  template to swap a blank chart card for a compact empty-state message. */
  hasStageData = false;
  hasPmData = false;
  hasCurrencyData = false;

  healthRows: HealthRow[] = [];

  private allProjects: PresalesProject[] = [];
  /** USD conversion rates (currency → USD-per-unit). Empty until loaded. */
  private rateMap = new Map<Currency, number>();

  constructor(
    private presalesService: PresalesService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    forkJoin({
      projects: this.presalesService.getAllProjects().pipe(catchError(() => of([] as PresalesProject[]))),
      rates:    this.presalesService.getCurrencyRates().pipe(catchError(() => of([] as CurrencyRate[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        // Include inactive projects so the KPIs (Won count, Win Rate) match what
        // the Inventory page reports. Historical Won/Lost deals still count
        // toward the win-rate calculation even after being archived.
        this.allProjects = data.projects || [];
        this.rateMap = buildRateMap(data.rates);
        this.buildAll();
        this.loading = false;
      });
  }

  // ================= Aggregations =================

  private buildAll(): void {
    this.cards = this.buildCards();
    this.stageChartData    = this.buildStageChart();
    this.pmChartData       = this.buildPmChart();
    this.currencyChartData = this.buildCurrencyChart();
    this.hasStageData      = this.chartHasData(this.stageChartData);
    this.hasPmData         = this.chartHasData(this.pmChartData);
    this.hasCurrencyData   = this.chartHasData(this.currencyChartData);
    this.healthRows        = this.buildHealthRows();
  }

  /** True when the chart has at least one positive data point. */
  private chartHasData(chart: { datasets?: { data?: number[] }[] }): boolean {
    const data = chart?.datasets?.[0]?.data ?? [];
    return data.some(v => (v ?? 0) > 0);
  }

  private buildCards(): KpiCard[] {
    const total = this.allProjects.length;
    const won  = this.allProjects.filter(p => p.salesStage === 'Won').length;
    const lost = this.allProjects.filter(p => p.salesStage === 'Lost').length;
    const active = this.allProjects.filter(p => p.salesStage !== 'Won' && p.salesStage !== 'Lost').length;
    const closed = won + lost;
    const winRate = closed === 0 ? 0 : Math.round((won / closed) * 100);

    // Convert open pipeline value to a single currency (USD) so the cards show
    // one comparable number instead of a per-currency string. Deals still hold
    // their native currency; unrated ones are excluded and counted below.
    let usdPipeline = 0;
    let usdWeighted = 0;
    let unrated = 0;
    let ratedOpen = 0;   // open deals with a known rate — denominator for avg deal size
    for (const p of this.allProjects) {
      if (p.salesStage === 'Won' || p.salesStage === 'Lost') continue;
      const pipe = toUsd(p.revenueTarget, p.currency, this.rateMap);
      const weight = toUsd((p.revenueTarget ?? 0) * (p.winningPercentage ?? 0) / 100, p.currency, this.rateMap);
      if (pipe == null) { unrated++; continue; }
      usdPipeline += pipe;
      usdWeighted += weight ?? 0;
      ratedOpen++;
    }
    const hasRates = this.rateMap.size > 0;
    const unratedNote = unrated > 0 ? ` · ${unrated} unrated excluded` : '';
    const avgDealSize = ratedOpen > 0 ? Math.round(usdPipeline / ratedOpen) : 0;

    // Open deals whose Expected Close date is in the current calendar month.
    const now = new Date();
    const closingThisMonth = this.allProjects.filter(p => {
      if (p.salesStage === 'Won' || p.salesStage === 'Lost' || !p.expectedCloseDate) return false;
      const d = new Date(p.expectedCloseDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;

    return [
      {
        label: 'Total Presales',
        value: `${total}`,
        sublabel: `${active} open · ${closed} closed`,
        icon: 'pi pi-briefcase',
        styleClass: 'stats-card--total',
      },
      {
        label: 'Active Pipeline',
        value: hasRates ? `≈ USD ${this.formatMoney(usdPipeline)}` : '—',
        sublabel: `Open deals (excl. Won / Lost)${unratedNote}`,
        icon: 'pi pi-chart-line',
        styleClass: 'stats-card--info',
      },
      {
        label: 'Weighted Value',
        value: hasRates ? `≈ USD ${this.formatMoney(usdWeighted)}` : '—',
        sublabel: 'Revenue × Winning %',
        icon: 'pi pi-chart-bar',
        styleClass: 'stats-card--warning',
      },
      {
        label: 'Avg Deal Size',
        value: hasRates ? `≈ USD ${this.formatMoney(avgDealSize)}` : '—',
        sublabel: 'Across open deals',
        icon: 'pi pi-dollar',
        styleClass: 'stats-card--total',
      },
      {
        label: 'Closing This Month',
        value: `${closingThisMonth}`,
        sublabel: 'By expected close date',
        icon: 'pi pi-calendar',
        styleClass: 'stats-card--info',
      },
      {
        label: 'Win Rate',
        value: `${winRate}%`,
        sublabel: `${won} won of ${closed} closed`,
        icon: 'pi pi-percentage',
        styleClass: 'stats-card--success',
      },
    ];
  }

  private buildStageChart() {
    const stages: SalesStage[] = ['New Idea', 'Lead', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];
    const counts = stages.map(s => this.allProjects.filter(p => p.salesStage === s).length);
    return {
      labels: stages.map((s, i) => `${s} (${counts[i]})`),
      datasets: [{
        data: counts,
        backgroundColor: ['#c4b5fd', '#94a3b8', '#0ea5e9', '#6366f1', '#f59e0b', '#10b981', '#ef4444'],
        borderWidth: 0,
      }],
    };
  }

  private buildPmChart() {
    const map = new Map<string, number>();
    for (const p of this.allProjects) {
      const name = p.managerName || 'Unassigned';
      map.set(name, (map.get(name) ?? 0) + 1);
    }
    // Top 8, roll the rest into "Others"
    const sorted = [...map.entries()].sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 8);
    const otherCount = sorted.slice(8).reduce((sum, [, n]) => sum + n, 0);
    const entries = otherCount > 0 ? [...top, ['Others', otherCount] as [string, number]] : top;

    const palette = ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#0ea5e9', '#38bdf8', '#7dd3fc', '#0284c7', '#94a3b8'];
    return {
      labels: entries.map(([name, n]) => `${name}: ${n}`),
      datasets: [{
        data: entries.map(([, n]) => n),
        backgroundColor: entries.map((_, i) => palette[i % palette.length]),
        borderWidth: 0,
      }],
    };
  }

  private buildCurrencyChart() {
    // Slice size = each currency's open pipeline value CONVERTED TO USD, so the
    // donut reflects true value share (a big AUD nominal no longer dwarfs a
    // higher-value USD deal). Labels keep the currency so the mix is still readable.
    const map = new Map<Currency, number>();
    for (const p of this.allProjects) {
      if (p.salesStage === 'Won' || p.salesStage === 'Lost') continue;
      const usd = toUsd(p.revenueTarget, p.currency, this.rateMap);
      if (usd == null) continue; // unrated currency — omit from the value chart
      map.set(p.currency, (map.get(p.currency) ?? 0) + usd);
    }
    const entries = [...map.entries()];
    const palette: Record<Currency, string> = {
      LKR: '#0d9488', USD: '#059669', AUD: '#ea580c', EUR: '#4338ca', SGD: '#db2777', GBP: '#7c3aed',
    };
    return {
      labels: entries.map(([c, v]) => `${c}: ≈$${this.formatMoney(v)}`),
      datasets: [{
        data: entries.map(([, v]) => Math.round(v)),
        backgroundColor: entries.map(([c]) => palette[c]),
        borderWidth: 0,
      }],
    };
  }

  private buildHealthRows(): HealthRow[] {
    // "Active pursuits" table — exclude Lost and inactive (archived) rows. KPI
    // cards above still include everything for accurate Win Rate.
    return this.allProjects
      .filter(p => p.isActive && p.salesStage !== 'Lost')
      .sort((a, b) => (b.winningPercentage ?? 0) - (a.winningPercentage ?? 0))
      .map(p => ({
        code:           p.projectCode || '—',
        name:           p.name,
        client:         p.presalesClientName || '—',
        pm:             p.managerName || '—',
        accountManager: p.accountManagerName || '—',
        stage:          p.salesStage,
        currency:       p.currency,
        revenue:        p.revenueTarget ?? 0,
        winning:        p.winningPercentage ?? 0,
        startDate:      p.startDate ?? null,
        endDate:        p.endDate ?? null,
        expectedClose:  p.expectedCloseDate ?? null,
      }));
  }

  // ================= Chart options =================

  private buildChartOptions() {
    return {
      cutout: '62%',
      plugins: {
        legend: {
          position: 'right',
          labels: {
            boxWidth: 10,
            font: { size: 11 },
            color: '#475569',
          },
        },
        tooltip: {
          backgroundColor: '#0f172a',
          titleColor: '#f1f5f9',
          bodyColor: '#cbd5e1',
          padding: 10,
          cornerRadius: 6,
        },
      },
      maintainAspectRatio: false,
    };
  }

  // ================= Formatters =================

  formatMoney(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return `${Math.round(n)}`;
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    // Compact format ("8 Jul 26") fits narrow date columns and keeps full
    // information (day + month + short year). The full date is still visible
    // on hover via the standard cell tooltip.
    return this.datePipe.transform(iso, 'd MMM yy') ?? '—';
  }

  stageSeverity(stage: SalesStage): 'info' | 'success' | 'warn' | 'secondary' | 'danger' {
    switch (stage) {
      case 'Won':         return 'success';
      case 'Lost':        return 'danger';
      case 'Negotiation': return 'warn';
      case 'Proposal':    return 'info';
      case 'Qualified':   return 'info';
      default:            return 'secondary';
    }
  }
}
